import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, KeyRound, ShieldCheck, Globe, AtSign, Trash2, Plus, Check, AlertTriangle, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api, apiError } from "../lib/api";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const Section = ({ icon: Icon, color, title, children, testid, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass rounded-2xl p-6" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="flex items-center justify-between w-full gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} />
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

const Row = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-900">{label}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
    {children}
  </div>
);

export const AdministrationPanel = ({ workspaceId, role, plan }) => {
  const canManage = role === "owner" || role === "admin";
  const isPaid = plan && plan !== "free";

  const [policy, setPolicy] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [security, setSecurity] = useState(null);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [senders, setSenders] = useState([]);
  const [newSender, setNewSender] = useState("");
  const [addingSender, setAddingSender] = useState(false);
  const [codeDialog, setCodeDialog] = useState(null); // { id, code }
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [p, s, si] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/password-policy`),
        api.get(`/workspaces/${workspaceId}/security`),
        api.get(`/workspaces/${workspaceId}/sender-identities`).catch(() => ({ data: [] })),
      ]);
      setPolicy(p.data.policy);
      setSecurity(s.data);
      setSenders(si.data || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const r = await api.put(`/workspaces/${workspaceId}/password-policy`, policy);
      setPolicy(r.data.policy);
      toast.success(tr("administration:panel.politiqueDeMotDe"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingPolicy(false);
    }
  };

  const toggle2fa = async (val) => {
    setSavingSecurity(true);
    try {
      const r = await api.put(`/workspaces/${workspaceId}/security`, { require_2fa: val });
      setSecurity((s) => ({ ...s, require_2fa: r.data.require_2fa }));
      toast.success(val ? tr("administration:panel.2faDesormaisObligatoirePour") : "2FA non obligatoire.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingSecurity(false);
    }
  };

  const addSender = async () => {
    if (!newSender.trim()) return;
    setAddingSender(true);
    try {
      const r = await api.post(`/workspaces/${workspaceId}/sender-identities`, { email: newSender.trim() });
      setSenders((prev) => [r.data, ...prev]);
      setNewSender("");
      setCodeDialog({ id: r.data.id, code: "" });
      toast.success(tr("administration:panel.unCodeDeVerification"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAddingSender(false);
    }
  };

  const verifyEmail = async () => {
    try {
      const r = await api.post(`/workspaces/${workspaceId}/sender-identities/${codeDialog.id}/verify-email`, { code: codeDialog.code });
      setSenders((prev) => prev.map((s) => (s.id === r.data.id ? r.data : s)));
      setCodeDialog(null);
      toast.success(tr("administration:panel.adresseExpeditriceVerifiee"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const verifyDns = async (id) => {
    try {
      const r = await api.post(`/workspaces/${workspaceId}/sender-identities/${id}/verify-dns`);
      setSenders((prev) => prev.map((s) => (s.id === r.data.id ? r.data : s)));
      toast.success(tr("administration:panel.enregistrementDnsVerifie"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeSender = async (id) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/sender-identities/${id}`);
      setSenders((prev) => prev.filter((s) => s.id !== id));
      toast.success(tr("administration:panel.adresseExpeditriceSupprimee"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const scheduleDeletion = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/workspaces/${workspaceId}`);
      setSecurity((s) => ({ ...s, deletion_scheduled_at: r.data.deletion_scheduled_at }));
      setDeleteDialog(false);
      toast.success(tr("administration:panel.suppressionPlanifieeVousAvez"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeletion = async () => {
    try {
      await api.post(`/workspaces/${workspaceId}/cancel-deletion`);
      setSecurity((s) => ({ ...s, deletion_scheduled_at: null }));
      toast.success(tr("administration:panel.suppressionAnnulee"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!policy || !security) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const POLICY_TOGGLES = [
    ["require_upper", tr("administration:panel.auMoinsUneMajuscule")],
    ["require_lower", tr("administration:panel.auMoinsUneMinuscule")],
    ["require_digit", tr("administration:panel.auMoinsUnChiffre")],
    ["require_special", tr("administration:panel.auMoinsUnCaractere")],
  ];

  return (
    <div className="space-y-6" data-testid="administration-panel">
      {/* Politique de mot de passe */}
      <Section icon={KeyRound} color="text-emerald-600" title={tr("administration:panel.politiqueDeMotDe2")} testid="password-policy-section">
        {!isPaid && (
          <div className="mb-4 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            {tr("administration:panel.laPolitiqueStrictePar")}
          </div>
        )}
        <div className="space-y-3">
          <Row label={tr("administration:panel.politiqueActivee")} hint={tr("administration:panel.appliqueeAuxChangementsDe")}>
            <Switch data-testid="policy-enabled" checked={!!policy.enabled} disabled={!canManage || !isPaid}
              onCheckedChange={(v) => setPolicy((p) => ({ ...p, enabled: v }))} />
          </Row>
          <Row label={tr("administration:panel.longueurMinimale")} hint={tr("administration:panel.entre8Et128")}>
            <Input data-testid="policy-min-length" type="number" min={8} max={128}
              value={policy.min_length} disabled={!canManage || !isPaid}
              onChange={(e) => setPolicy((p) => ({ ...p, min_length: parseInt(e.target.value || "8", 10) }))}
              className="w-24 bg-white border-slate-200" />
          </Row>
          {POLICY_TOGGLES.map(([k, lbl]) => (
            <Row key={k} label={lbl}>
              <Switch data-testid={`policy-${k}`} checked={!!policy[k]} disabled={!canManage || !isPaid}
                onCheckedChange={(v) => setPolicy((p) => ({ ...p, [k]: v }))} />
            </Row>
          ))}
          <Row label={tr("administration:panel.antiReutilisation")} hint={tr("administration:panel.nombreDeDerniersMots")}>
            <Input data-testid="policy-reuse" type="number" min={0} max={24}
              value={policy.prevent_reuse_last} disabled={!canManage || !isPaid}
              onChange={(e) => setPolicy((p) => ({ ...p, prevent_reuse_last: parseInt(e.target.value || "0", 10) }))}
              className="w-24 bg-white border-slate-200" />
          </Row>
          <Row label={tr("administration:panel.expirationRotation")} hint={tr("administration:panel.joursAvantExpiration0")}>
            <Input data-testid="policy-max-age" type="number" min={0} max={3650}
              value={policy.max_age_days} disabled={!canManage || !isPaid}
              onChange={(e) => setPolicy((p) => ({ ...p, max_age_days: parseInt(e.target.value || "0", 10) }))}
              className="w-24 bg-white border-slate-200" />
          </Row>
          {canManage && isPaid && (
            <Button data-testid="save-policy-btn" onClick={savePolicy} disabled={savingPolicy}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {savingPolicy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {tr("administration:panel.enregistrerLaPolitique")}
            </Button>
          )}
        </div>
      </Section>

      {/* Politique 2FA */}
      <Section icon={ShieldCheck} color="text-cyan-600" title={tr("administration:panel.doubleAuthentification2fa")} testid="twofa-policy-section">
        <Row label={tr("administration:panel.exigerLe2faPour")}
          hint={tr("administration:panel.chaqueMembreDevraActiver")}>
          <Switch data-testid="require-2fa-toggle" checked={!!security.require_2fa}
            disabled={!canManage || savingSecurity}
            onCheckedChange={toggle2fa} />
        </Row>
      </Section>

      {/* Zone de danger : suppression de l'espace */}
      {role === "owner" && (
        <Section icon={AlertTriangle} color="text-red-500" title={tr("administration:panel.supprimerCetEspace")} testid="danger-zone-section">
          {security.deletion_scheduled_at ? (
            <div className="space-y-3">
              <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                {tr("administration:panel.suppressionPlanifieeLe")}{" "}
                <b data-testid="deletion-date">{new Date(security.deletion_scheduled_at).toLocaleDateString(intlLocaleFor(i18n.language))}</b>{tr("administration:panel.lEspaceRestePleinement")}
              </div>
              <Button data-testid="cancel-deletion-btn" onClick={cancelDeletion} variant="outline" className="border-slate-200">
                {tr("administration:panel.annulerLaSuppression")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {tr("administration:panel.laSuppressionEstDifferee")}
              </p>
              <Button data-testid="delete-workspace-btn" onClick={() => setDeleteDialog(true)}
                className="bg-red-500 hover:bg-red-400 text-white font-semibold">
                <Trash2 className="w-4 h-4 mr-2" /> {tr("administration:panel.planifierLaSuppression")}
              </Button>
            </div>
          )}
        </Section>
      )}

      {/* Dialog : code de vérification e-mail expéditeur */}
      <Dialog open={!!codeDialog} onOpenChange={(o) => !o && setCodeDialog(null)}>
        <DialogContent data-testid="sender-code-dialog">
          <DialogHeader>
            <DialogTitle>{tr("administration:panel.verifierLAdresseExpeditrice")}</DialogTitle>
            <DialogDescription>{tr("administration:panel.saisissezLeCodeA")}</DialogDescription>
          </DialogHeader>
          <Input data-testid="sender-code-input" value={codeDialog?.code || ""} maxLength={6}
            onChange={(e) => setCodeDialog((d) => ({ ...d, code: e.target.value }))}
            placeholder="123456" className="bg-white border-slate-200 text-center tracking-widest" />
          <DialogFooter>
            <Button data-testid="sender-code-submit" onClick={verifyEmail} disabled={(codeDialog?.code || "").length !== 6}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">{tr("administration:panel.verifier")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog : confirmation suppression */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="delete-workspace-dialog">
          <DialogHeader>
            <DialogTitle>{tr("administration:panel.planifierLaSuppressionDe")}</DialogTitle>
            <DialogDescription>
              {tr("administration:panel.lEspaceSeraSupprime")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setDeleteDialog(false)}>{tr("administration:panel.annuler")}</Button>
            <Button data-testid="confirm-delete-workspace-btn" onClick={scheduleDeletion} disabled={deleting}
              className="bg-red-500 hover:bg-red-400 text-white font-semibold">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {tr("administration:panel.confirmer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
